/**
 * Campo de formulario: etiqueta arriba (14 px/500), texto de ayuda (12 px) y error (12 px con
 * icono), conectados por `aria-describedby` / `aria-invalid` (design-system §7 y §10).
 */
import * as LabelPrimitive from '@radix-ui/react-label';
import { CircleAlert } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface FieldProps {
  label: string;
  /** Recibe los atributos que debe llevar el control (id, aria-*). */
  children: (controlProps: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': true | undefined;
    'aria-required': true | undefined;
  }) => ReactNode;
  help?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

export function Field({ label, children, help, error, required, className }: FieldProps) {
  const id = useId();
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-fm-1', className)}>
      <LabelPrimitive.Root htmlFor={id} className="text-fm-sm font-medium text-fm-text">
        {label}
        {required ? (
          <span className="text-fm-primary-text" aria-hidden="true">
            {' *'}
          </span>
        ) : null}
      </LabelPrimitive.Root>

      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required ? true : undefined,
      })}

      {help ? (
        <p id={helpId} className="text-fm-xs text-fm-text-secondary">
          {help}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="flex items-center gap-fm-1 text-fm-xs text-fm-danger-text">
          <CircleAlert size={12} aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  );
}
